import React, { useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Clock, User, ChevronDown, ChevronRight, Copy, Check,
  X, MessageSquare, Monitor, ExternalLink, AlertTriangle, FileDown,
} from "lucide-react";
import {
  getSettings, generateFollowUpMessage, PLAN_DATA, FOLLOW_UP_TEMPLATES,
} from "./demoConfig";
import { CASE_STUDIES } from "./DemoScreens";
import { generateDemoSummaryPDF } from "./generatePDF";

const OBJECTIONS = [
  { q: "It's too expensive", a: "It's $750 today — less than a week of your target salary. The other $750 only comes after we book your first interview. You're not paying $1,499 for a promise. You're paying $750 to start, and $750 when it's already working." },
  { q: "I can apply myself", a: "Of course — but you're doing APPS/week. We do 780/month plus automation. That volume difference turns a 6-month search into a 6-week one." },
  { q: "I need to think about it", a: "Totally fair. What specifically would help you decide — the price, the approach, or something else? I can address that right now." },
  { q: "Already working with a recruiter", a: "Great — we complement recruiters. They cover 5-10 roles. We cover 780 more that they don't touch. It's additive, not competing." },
  { q: "What if it doesn't work?", a: "Our Guaranteed plan has split payment — $750 today, and $750 only after we book your first interview. We keep applying until you have 10 interviews. No time limit. You only pay the second half when the product is already working." },
  { q: "Need to discuss with partner", a: "Makes sense. Want me to send a summary? Most partners just want to see the ROI math — I'll include it." },
  { q: "Not sure about automated apps", a: "Good concern — we don't automate the applications. Real humans apply with tailored cover letters. ChillApply handles tracking and follow-ups only." },
  { q: "Why not LinkedIn Easy Apply?", a: "Easy Apply averages 0.5% response because everyone uses it. We target company career pages and hidden boards where competition is 10x lower." },
];

export default function CloserSidebar({ state, actions, elapsed, screenTitles, closerTitles, popoutMode, onPopout }) {
  const { lead, currentScreen, auth, settings, weeklyApps, confirmedRole, confirmedMarket, linkedInConnections, linkedInReferrals, monthsSearching, salaryInput, monthsInput, gmailMode, revealedPanels, revealedWays, revealedStackItems, recommendedPlan, selectedPlan, offerPhase, selectedCaseStudies, proofTab, pdfPage, pdfTotalPages, paymentMethod } = state;
  const [objOpen, setObjOpen] = useState(currentScreen === 12);
  const [activeObj, setActiveObj] = useState(null);
  const [followUpCopied, setFollowUpCopied] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showPainPoints, setShowPainPoints] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState("immediate");

  const plan = (selectedPlan || recommendedPlan) ? PLAN_DATA[selectedPlan || recommendedPlan] : null;

  const painPointTags = selectedCaseStudies.map((id) => {
    const cs = CASE_STUDIES.find((c) => c.id === id);
    return cs ? cs.tag : null;
  }).filter(Boolean);

  const getActiveMessage = () => {
    const template = FOLLOW_UP_TEMPLATES[activeTemplate];
    if (!template) return "";
    const leadData = { ...lead, recommendedPlan: selectedPlan || recommendedPlan, targetSalary: parseFloat(salaryInput), monthsSearching: parseFloat(monthsInput) };
    return template.generate(leadData, auth.name, settings, painPointTags);
  };

  const copyFollowUp = () => {
    const msg = getActiveMessage();
    navigator.clipboard.writeText(msg);
    setFollowUpCopied(true);
    setTimeout(() => setFollowUpCopied(false), 2000);
  };

  const fillObjText = (text) => {
    let result = text
      .replace("$SALARY", `$${parseFloat(salaryInput) ? parseFloat(salaryInput).toLocaleString() : "X"}`)
      .replace("APPS", weeklyApps || "X");
    if (painPointTags.length > 0) {
      result += ` You mentioned struggling with ${painPointTags.slice(0, 2).join(" and ").toLowerCase()} — that's exactly what this solves.`;
    }
    return result;
  };

  return (
    <div className="closer-sidebar" data-testid="closer-sidebar">
      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-header-top">
          <span className="sidebar-timer"><Clock className="w-3.5 h-3.5" /> {elapsed}</span>
          <div className="sidebar-header-btns">
            {!popoutMode && onPopout && (
              <Button size="sm" variant="outline" onClick={onPopout} data-testid="popout-btn" className="sidebar-popout-btn">
                <ExternalLink className="w-3.5 h-3.5" /> Pop Out
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={actions.endDemo} data-testid="end-demo-btn" className="sidebar-end-btn">
              <X className="w-3.5 h-3.5" /> End
            </Button>
          </div>
        </div>
        <div className="sidebar-lead-card">
          <User className="w-4 h-4" />
          <div>
            <div className="sidebar-lead-name">{lead.clientName} {lead.clientLastName}</div>
            <div className="sidebar-lead-info">{lead.targetRole} · {lead.targetMarket}</div>
            {painPointTags.length > 0 && (
              <div className="sidebar-pain-tags">
                {painPointTags.map((tag, i) => (<span key={i} className="sidebar-pain-tag">{tag}</span>))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Screen nav */}
      <div className="sidebar-nav">
        {(closerTitles || screenTitles).map((title, i) => (
          <button key={i} className={`sidebar-nav-step ${i + 1 === currentScreen ? "nav-active" : ""} ${i + 1 < currentScreen ? "nav-done" : ""}`} onClick={() => actions.setCurrentScreen(i + 1)} data-testid={`nav-step-${i + 1}`}>
            <span className="nav-step-num">{i + 1}</span>
            <span className="nav-step-title">{title}</span>
          </button>
        ))}
      </div>

      {/* Screen-specific controls */}
      <div className="sidebar-controls">
        <ScreenControls state={state} actions={actions} painPointTags={painPointTags} />
      </div>

      {/* Pain points reference */}
      {selectedCaseStudies.length > 0 && (
        <div className="sidebar-section">
          <button className="sidebar-section-toggle" onClick={() => setShowPainPoints(!showPainPoints)} data-testid="painpoints-toggle">
            <AlertTriangle className="w-4 h-4" />
            <span>Their Pain Points ({selectedCaseStudies.length})</span>
            {showPainPoints ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
          </button>
          {showPainPoints && (
            <div className="painpoints-list" data-testid="painpoints-list">
              {selectedCaseStudies.map((id) => {
                const cs = CASE_STUDIES.find((c) => c.id === id);
                if (!cs) return null;
                return (<div key={id} className="painpoint-item"><span className="painpoint-tag-sm">{cs.tag}</span><span className="painpoint-text">{cs.painPoint}</span></div>);
              })}
            </div>
          )}
        </div>
      )}

      {/* Objection handler */}
      <div className="sidebar-section">
        <button className="sidebar-section-toggle" onClick={() => setObjOpen(!objOpen)} data-testid="objection-toggle">
          <MessageSquare className="w-4 h-4" />
          <span>Objection Handler</span>
          {objOpen ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
        </button>
        {objOpen && (
          <div className="objection-list" data-testid="objection-list">
            {OBJECTIONS.map((obj, i) => (
              <div key={i} className="objection-item">
                <button className={`objection-q ${activeObj === i ? "objection-q-active" : ""}`} onClick={() => setActiveObj(activeObj === i ? null : i)} data-testid={`objection-${i}`}>
                  "{obj.q}"
                </button>
                {activeObj === i && <div className="objection-a">{fillObjText(obj.a)}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Follow-up */}
      <div className="sidebar-section">
        <button className="sidebar-section-toggle" onClick={() => setShowFollowUp(!showFollowUp)} data-testid="followup-toggle">
          <Copy className="w-4 h-4" />
          <span>Follow-Up Messages</span>
          {showFollowUp ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
        </button>
        {showFollowUp && (
          <div className="followup-section" data-testid="followup-section">
            {(selectedPlan || recommendedPlan) && (
              <div className="followup-plan-info">Plan: <strong>{PLAN_DATA[selectedPlan || recommendedPlan]?.name}</strong> — {PLAN_DATA[selectedPlan || recommendedPlan]?.price}</div>
            )}
            {painPointTags.length > 0 && (
              <div className="followup-pain-info">Pain: {painPointTags.slice(0, 3).map((t, i) => <span key={i} className="followup-pain-chip">{t}</span>)}</div>
            )}
            <div className="followup-templates" data-testid="followup-templates">
              {Object.entries(FOLLOW_UP_TEMPLATES).map(([key, tmpl]) => (
                <button key={key} className={`followup-tmpl-btn ${activeTemplate === key ? "followup-tmpl-active" : ""}`} onClick={() => setActiveTemplate(key)} data-testid={`followup-tmpl-${key}`} title={tmpl.desc}>
                  <span className="followup-tmpl-emoji">{tmpl.emoji}</span>
                  <span className="followup-tmpl-label">{tmpl.label}</span>
                </button>
              ))}
            </div>
            <div className="followup-tmpl-desc">{FOLLOW_UP_TEMPLATES[activeTemplate]?.desc}</div>
            <div className="whatsapp-bubble sidebar-bubble"><pre className="whatsapp-text">{getActiveMessage()}</pre></div>
            <Button size="sm" onClick={copyFollowUp} className="followup-copy-btn" data-testid="copy-followup-btn">
              {followUpCopied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy to clipboard</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ScreenControls({ state, actions, painPointTags }) {
  const { currentScreen, weeklyApps, confirmedRole, confirmedMarket, linkedInConnections, linkedInReferrals, monthsSearching, gmailMode, revealedPanels, revealedWays, revealedStackItems, salaryInput, monthsInput, settings, selectedCaseStudies, proofTab, pdfPage, pdfTotalPages, selectedPlan, recommendedPlan, offerPhase, paymentMethod, lead } = state;

  // Screen 1: Welcome
  if (currentScreen === 1) {
    return (
      <div className="screen-ctrl" data-testid="screen-1-controls">
        <h4 className="ctrl-title">Opening Script</h4>
        <div className="ctrl-script">
          <p className="ctrl-script-line">"Hey {lead.clientName}! Thanks for taking the time today. Quick intro — I'm going to walk you through something that's helped 1500+ candidates land interviews in weeks, not months."</p>
          <p className="ctrl-script-line">"Before we start — what's the #1 thing that's been frustrating you about your job search right now?"</p>
          <p className="ctrl-script-line"><strong>Let them vent. Listen. Take a mental note of their exact words — you'll mirror these back later.</strong></p>
          <p className="ctrl-script-line">"Got it. Let me show you exactly why that's happening — and what the fix looks like."</p>
        </div>
        <ul className="ctrl-tips">
          <li>Their frustration is your ammunition for the close. Remember their exact words.</li>
          <li>60 seconds max on this slide. Build curiosity, not a lecture.</li>
        </ul>
      </div>
    );
  }

  // Screen 2: Qualify
  if (currentScreen === 2) {
    return (
      <div className="screen-ctrl" data-testid="screen-2-controls">
        <h4 className="ctrl-title">Diagnose as they talk</h4>
        <div className="ctrl-field"><Label>Target role</Label><Input value={confirmedRole} onChange={(e) => actions.setConfirmedRole(e.target.value)} data-testid="ctrl-role" /></div>
        <div className="ctrl-field"><Label>Target market</Label><Input value={confirmedMarket} onChange={(e) => actions.setConfirmedMarket(e.target.value)} data-testid="ctrl-market" /></div>
        <div className="ctrl-field"><Label>Weekly applications</Label><Input type="number" value={weeklyApps} onChange={(e) => actions.setWeeklyApps(e.target.value)} placeholder="e.g. 10" data-testid="ctrl-weekly-apps" /></div>
        <div className="ctrl-field"><Label>LinkedIn connections/week</Label><Input type="number" value={linkedInConnections} onChange={(e) => actions.setLinkedInConnections(e.target.value)} placeholder="e.g. 5" data-testid="ctrl-linkedin-conn" /></div>
        <div className="ctrl-field"><Label>Referral requests/week</Label><Input type="number" value={linkedInReferrals} onChange={(e) => actions.setLinkedInReferrals(e.target.value)} placeholder="e.g. 2" data-testid="ctrl-linkedin-ref" /></div>
        <div className="ctrl-field"><Label>Months searching</Label><Input type="number" value={monthsSearching} onChange={(e) => actions.setMonthsSearching(e.target.value)} placeholder="e.g. 3" data-testid="ctrl-months-searching" /></div>
        <ul className="ctrl-tips">
          <li>"I want to understand exactly where you are so everything I show you is 100% specific to YOUR situation."</li>
          <li>Ask: "What role are you targeting?" Fill it in as they answer.</li>
          <li>"How many jobs are you applying to per week?" — this is crucial. Low numbers = your leverage.</li>
          <li>"How long have you been searching?" — if 3+ months, lean into the pain: "That's a lot of time. Let me show you why."</li>
          <li>When done: "Okay {lead.clientName}, now let me show you what these numbers actually produce in the real world. This part is eye-opening."</li>
        </ul>
      </div>
    );
  }

  // Screen 3: Numbers Game
  if (currentScreen === 3) {
    return (
      <div className="screen-ctrl" data-testid="screen-3-controls">
        <h4 className="ctrl-title">Gap Reveal Script</h4>
        <div className="ctrl-script">
          <p className="ctrl-script-line">"Look at the left side. That's you right now — {weeklyApps || '10'} applications a week, doing everything manually."</p>
          <p className="ctrl-script-line">"Now look at the right side. Same resume, same market, same you. The ONLY difference is volume."</p>
          <p className="ctrl-script-line"><strong>Pause. Point at the callback numbers. Let the gap hit them.</strong></p>
          <p className="ctrl-script-line">"This isn't a motivation problem, {lead.clientName}. It's a math problem. And math has a solution."</p>
          <p className="ctrl-script-line">"And the best part? You put in zero effort. You focus on interview prep, upskilling, whatever matters to you — while the system runs."</p>
        </div>
        <ul className="ctrl-tips">
          <li>This is the MOST important slide. If they don't feel the gap, nothing after this works.</li>
          <li>Ask: "Does that gap surprise you?" — Wait for the answer.</li>
          <li>If they say "not really" → "Then you already know the answer. The only question is whether you keep going alone or let us fix the math."</li>
        </ul>
      </div>
    );
  }

  // Screen 4: Proof Wall
  if (currentScreen === 4) {
    return (
      <div className="screen-ctrl" data-testid="screen-4-controls">
        <h4 className="ctrl-title">Proof Wall Controls</h4>
        <div className="ctrl-script">
          <p className="ctrl-script-line">"Now let me show you proof this actually works. These aren't testimonials we wrote — these are real WhatsApp conversations with real clients."</p>
          <p className="ctrl-script-line">"Take a look at these stories. Which one sounds most like your situation?"</p>
          <p className="ctrl-script-line">Open the Gmail inbox to show live interview replies. Let it land. Then come back.</p>
        </div>
        {gmailMode ? (
          <div className="gmail-sidebar-alert" data-testid="gmail-sidebar-alert">
            <Monitor className="w-4 h-4" />
            <div><strong>Switch to Gmail tab now</strong><p>Show for 20-30 seconds. Click 'Back' when done.</p></div>
            <Button size="sm" onClick={() => actions.setGmailMode(false)} data-testid="back-from-gmail" className="gmail-back-btn">Back from Gmail</Button>
          </div>
        ) : (
          <>
            <div className="ctrl-proof-tabs">
              <Button size="sm" variant={proofTab === "pain" ? "default" : "outline"} onClick={() => actions.setProofTab("pain")} className={proofTab === "pain" ? "ctrl-tab-active" : ""}>Stories</Button>
              <Button size="sm" variant={proofTab === "results" ? "default" : "outline"} onClick={() => actions.setProofTab("results")} className={proofTab === "results" ? "ctrl-tab-active" : ""}>PDF Results</Button>
            </div>
            {proofTab === "pain" && (
              <>
                <p className="ctrl-note">Ask: "Which of these sounds like your situation?" Client picks 2-3.</p>
                {selectedCaseStudies.length > 0 && (
                  <div className="ctrl-selected-cases">
                    <span className="ctrl-selected-label">{selectedCaseStudies.length} pain points:</span>
                    {selectedCaseStudies.map((id) => { const cs = CASE_STUDIES.find((c) => c.id === id); return cs ? <span key={id} className="ctrl-selected-tag">{cs.tag}</span> : null; })}
                  </div>
                )}
                {selectedCaseStudies.length > 0 && (
                  <p className="ctrl-note">After they pick one: "What specifically made that feel familiar?" Let them talk.</p>
                )}
              </>
            )}
            {proofTab === "results" && (
              <div className="ctrl-pdf-nav"><span className="ctrl-note">Page {pdfPage} of {pdfTotalPages || "..."}</span><div className="ctrl-pdf-btns"><Button size="sm" variant="outline" disabled={pdfPage <= 1} onClick={() => actions.setPdfPage(pdfPage - 1)}>Prev</Button><Button size="sm" variant="outline" disabled={pdfPage >= (pdfTotalPages || 50)} onClick={() => actions.setPdfPage(pdfPage + 1)}>Next</Button></div></div>
            )}
          </>
        )}
      </div>
    );
  }

  // Screen 5: 3 Ways
  if (currentScreen === 5) {
    return (
      <div className="screen-ctrl" data-testid="screen-5-controls">
        <h4 className="ctrl-title">3 Ways Education</h4>
        <div className="reveal-buttons">
          {["Referrals", "Applications", "Recruiters"].map((name, i) => (
            <Button key={i} size="sm" variant={revealedWays[i] ? "outline" : "default"} disabled={revealedWays[i]} onClick={() => actions.revealWay(i)} className={revealedWays[i] ? "reveal-done" : "reveal-btn"} data-testid={`reveal-way-btn-${i}`}>
              {revealedWays[i] ? <><Check className="w-3.5 h-3.5" /> {name}</> : <>Reveal: {name}</>}
            </Button>
          ))}
        </div>
        <div className="ctrl-script">
          <p className="ctrl-script-line">"There are literally only 3 ways anyone on this planet gets a job interview. I'm going to show you all three."</p>
          <p className="ctrl-script-line"><strong>Reveal one at a time. Pause 3-5 seconds between each.</strong></p>
          <p className="ctrl-script-line">"Most people are doing one of these, poorly. We do all three, simultaneously, at a volume you can't match alone."</p>
          <p className="ctrl-script-line">"And {lead.clientName} — you don't manage any of it. You show up to interviews. That's it."</p>
        </div>
      </div>
    );
  }

  // Screen 6: Our System
  if (currentScreen === 6) {
    return (
      <div className="screen-ctrl" data-testid="screen-6-controls">
        <h4 className="ctrl-title">System Reveal</h4>
        <div className="reveal-buttons">
          {["ChillRefer", "ChillApply + Human", "LinkedIn Opt."].map((name, i) => (
            <Button key={i} size="sm" variant={revealedPanels[i] ? "outline" : "default"} disabled={revealedPanels[i]} onClick={() => actions.revealPanel(i)} className={revealedPanels[i] ? "reveal-done" : "reveal-btn"} data-testid={`reveal-btn-${i}`}>
              {revealedPanels[i] ? <><Check className="w-3.5 h-3.5" /> {name}</> : <>Reveal: {name}</>}
            </Button>
          ))}
        </div>
        <div className="ctrl-script">
          <p className="ctrl-script-line">"Now let me show you exactly HOW we cover each path."</p>
          <p className="ctrl-script-line"><strong>Reveal one panel at a time. After each one:</strong></p>
          <p className="ctrl-script-line">"This runs 24/7 in the background. You don't touch it. You don't manage it. It just works."</p>
          <p className="ctrl-script-line">After all 3: "Imagine waking up tomorrow and all three of these engines are already running for you."</p>
        </div>
        <ul className="ctrl-tips">
          <li>If they ask "how does ChillApply work?" → click "Open Live App" to show the real product</li>
          <li>After the banner: "While you're sitting in your next interview — all three of these are STILL running." Let that land.</li>
        </ul>
      </div>
    );
  }

  // Screen 7: Day 1 Process
  if (currentScreen === 7) {
    return (
      <div className="screen-ctrl" data-testid="screen-7-controls">
        <h4 className="ctrl-title">Day 1 Process Script</h4>
        <div className="ctrl-script">
          <p className="ctrl-script-line">"Let me walk you through exactly what happens from the moment you start."</p>
          <p className="ctrl-script-line">Walk through each step slowly. Use "YOUR" for everything — "Your assistant. Your WhatsApp group. Your dashboard."</p>
          <p className="ctrl-script-line"><strong>After Day 3 step:</strong> "{lead.clientName}, what would it feel like to wake up on Day 3 and see 30 applications went out with your name on them — without you lifting a finger?"</p>
          <p className="ctrl-script-line"><strong>STOP. Let them answer. This is where buy-in happens.</strong></p>
        </div>
        <ul className="ctrl-tips">
          <li>They should feel like they're ALREADY enrolled by the end of this slide</li>
          <li>Future-pace: "Picture yourself checking this dashboard on a Tuesday morning..."</li>
          <li>Don't ask "does this make sense?" — ask "can you see yourself using this?"</li>
        </ul>
      </div>
    );
  }

  // Screen 8: Dashboard
  if (currentScreen === 8) {
    return (
      <div className="screen-ctrl" data-testid="screen-8-controls">
        <h4 className="ctrl-title">Dashboard Script</h4>
        <div className="ctrl-script">
          <p className="ctrl-script-line">"This is YOUR dashboard. Every single application, every company, every resume used — all visible in real time."</p>
          <p className="ctrl-script-line">"Imagine opening this on a Tuesday morning and seeing 47 applications went out yesterday while you were sleeping."</p>
          <p className="ctrl-script-line"><strong>Point to: total applications counter, assigned assistant name, success rate.</strong></p>
          <p className="ctrl-script-line">"No more guessing. No more wondering 'did they even apply?' Everything is right here."</p>
        </div>
        <ul className="ctrl-tips">
          <li>This slide builds trust through transparency. Spend 60-90 seconds here.</li>
          <li>If they ask to see the real dashboard — offer to open it live</li>
          <li>Transition: "Now let me show you everything you get working for you..."</li>
        </ul>
      </div>
    );
  }

  // Screen 9: The Stack
  if (currentScreen === 9) {
    return (
      <div className="screen-ctrl" data-testid="screen-9-controls">
        <h4 className="ctrl-title">The Stack — Build the Value</h4>
        <div className="stack-reveal-controls">
          <Button size="sm" disabled={revealedStackItems >= 7} onClick={actions.revealNextStackItem} className="reveal-btn" data-testid="reveal-stack-btn">
            {revealedStackItems >= 7 ? <><Check className="w-3.5 h-3.5" /> All revealed</> : <>Reveal next ({revealedStackItems}/7)</>}
          </Button>
        </div>
        <div className="ctrl-script">
          <p className="ctrl-script-line"><strong>Reveal ONE at a time. Pause 3-5 seconds between each.</strong></p>
          <p className="ctrl-script-line">After item 2: "Just these two alone would cost you $500-800/month if you hired separately."</p>
          <p className="ctrl-script-line">After item 5: "That's a full dedicated team. Most people hire a career coach for $200/hour and get none of this."</p>
          <p className="ctrl-script-line">After all 7: "If you were to build this yourself — hiring an assistant, buying the tools, getting the resume done — you're looking at $2,000-3,000 minimum."</p>
          <p className="ctrl-script-line"><strong>When the closing question appears — say NOTHING. Let them process.</strong></p>
          <p className="ctrl-script-line">Then: "Let me show you what this actually costs vs what you're losing right now."</p>
        </div>
      </div>
    );
  }

  // Screen 10: ROI
  if (currentScreen === 10) {
    return (
      <div className="screen-ctrl" data-testid="screen-10-controls">
        <h4 className="ctrl-title">ROI — The Money Slide</h4>
        <div className="ctrl-field"><Label>Monthly salary ($)</Label><Input type="number" value={salaryInput} onChange={(e) => actions.setSalaryInput(e.target.value)} placeholder="6000" data-testid="ctrl-salary" /></div>
        <div className="ctrl-field"><Label>Months searching</Label><Input type="number" value={monthsInput} onChange={(e) => actions.setMonthsInput(e.target.value)} placeholder="3" data-testid="ctrl-months" /></div>
        <div className="ctrl-script">
          <p className="ctrl-script-line">"Let me ask you something, {lead.clientName}. What's your expected monthly salary?" Let them say the number.</p>
          <p className="ctrl-script-line">"So every month you're not employed, that's ${salaryInput || '___'} that's NOT hitting your bank account."</p>
          <p className="ctrl-script-line"><strong>Point to the left side (DIY path):</strong> "This is the path you're on right now. {monthsInput || '3'}+ more months, ${salaryInput ? (parseInt(salaryInput) * (parseInt(monthsInput || 3) + 3)).toLocaleString() : '___'} in lost salary, hundreds of hours grinding."</p>
          <p className="ctrl-script-line"><strong>Point to the right side:</strong> "This is what happens when you let us handle it. You invest ${selectedPlan ? PLAN_DATA[selectedPlan]?.price : '$399-$625'} now and start earning ${salaryInput || '___'}/month within weeks."</p>
          <p className="ctrl-script-line">"The question isn't whether you can afford this. The question is whether you can afford NOT to."</p>
        </div>
      </div>
    );
  }

  // Screen 11: Offer / Pricing
  if (currentScreen === 11) {
    const planKey = selectedPlan || recommendedPlan;
    const recPlan = recommendedPlan ? PLAN_DATA[recommendedPlan] : null;
    return (
      <div className="screen-ctrl" data-testid="screen-11-controls">
        <h4 className="ctrl-title">Offer / Pricing</h4>
        <div className="ctrl-script">
          <p className="ctrl-script-line">"These are our 3 plans. Take a look — the Most Popular tag is on the 2-month plan because that's what most clients in your situation choose."</p>
          <p className="ctrl-script-line"><strong>Stay quiet. Let them read. Don't sell.</strong></p>
          <p className="ctrl-script-line">If they ask "which one should I pick?" → click the "Which plan fits me best?" button on THEIR screen. Let the AI recommend.</p>
          <p className="ctrl-script-line">If they don't ask, wait 15 seconds then: "Any questions about the plans? Take your time."</p>
        </div>
        {selectedPlan && PLAN_DATA[selectedPlan] && (
          <div className="ctrl-rec-card"><strong>{PLAN_DATA[selectedPlan].name}</strong><span>{PLAN_DATA[selectedPlan].price}</span></div>
        )}
        <ul className="ctrl-tips">
          <li>Never say "I recommend..." — let the system recommend for you</li>
          <li>If they pick the $625: "Great choice — that's our Most Popular plan. Most clients land in weeks 5–8."</li>
          <li>If they pick the $399: "Smart — you'll have the full team from Day 1"</li>
          <li>If they pick the $1,499: "That's the no-risk choice — $750 today, $750 only after we book your first interview. We don't stop until you have 10 interviews."</li>
          <li>Whatever they pick, validate it immediately. Never upsell on this slide.</li>
        </ul>
      </div>
    );
  }

  // Screen 12: Close & Payment
  if (currentScreen === 12) {
    const handleGeneratePDF = () => {
      const leadData = {
        ...lead,
        selectedPlan: state.selectedPlan || state.recommendedPlan,
        recommendedPlan: state.recommendedPlan,
        targetSalary: parseFloat(state.salaryInput),
        monthsSearching: parseFloat(state.monthsInput),
      };
      const s = getSettings();
      generateDemoSummaryPDF(leadData, s, state.auth, state.selectedCaseStudies);
    };

    return (
      <div className="screen-ctrl" data-testid="screen-12-controls">
        <h4 className="ctrl-title">Close & Payment</h4>
        <div className="ctrl-script">
          <p className="ctrl-script-line"><strong>"So {lead.clientName}, does this make sense for your situation?"</strong></p>
          <p className="ctrl-script-line"><strong>STOP. Say nothing. The first person to speak loses. Wait them out — even if it's 10 seconds of silence.</strong></p>
          <p className="ctrl-script-line">If YES → "Perfect. How would you like to pay?" → select payment → send link → stay on call until confirmed.</p>
          <p className="ctrl-script-line">If HESITATION → "I totally understand. What's holding you back?" → Listen → Address the specific objection below.</p>
          {(selectedPlan === "guaranteed" || recommendedPlan === "guaranteed") && (
            <p className="ctrl-script-line" style={{ background: "#0F2A21", borderLeft: "3px solid #1D9E75", paddingLeft: "10px" }}>
              <strong>Guaranteed plan close:</strong> "$750 today — your team is assembled within the hour, resume rebuild starts immediately, applications go live Day 3. $750 only after we book your first interview. We keep going until you have 10 interviews. {lead.clientName}, what's the risk here?"
            </p>
          )}
        </div>
        {painPointTags.length > 0 && (
          <div className="ctrl-pain-reminder"><strong>Mirror their words from Slide 1:</strong> "{painPointTags.join(", ")}" — "This is exactly what you told me was frustrating you. This solves it."</div>
        )}
        <ul className="ctrl-tips">
          <li><strong>"Let me think"</strong> → "Totally fair. What specifically do you want to think about? Is it the price, the process, or something else?" (Isolate the objection)</li>
          {(selectedPlan === "guaranteed" || recommendedPlan === "guaranteed") ? (
            <li><strong>"It's too expensive"</strong> → "It's $750 today, not $1,499. The other $750 only comes after we book your first interview. You're paying half today, and the other half only when it's already working."</li>
          ) : (
            <li><strong>"It's too expensive"</strong> → "I get it. But you said you're targeting ${salaryInput || '___'}/month. That's ${salaryInput ? (parseInt(salaryInput) * 3).toLocaleString() : '___'} over 3 months of searching alone. This plan is less than a week of your target salary."</li>
          )}
          <li><strong>"I need to talk to spouse/partner"</strong> → "Of course! What do you think they'd say if you told them you could start getting interviews this week?"</li>
          <li><strong>"Can I try on my own first?"</strong> → "You've been trying for {monthsInput || '—'} months. What's going to be different next month?"</li>
        </ul>
        <div className="ctrl-scarcity-note">
          <strong>Last resort (use once):</strong> "We have one assistant slot opening up this week. If you want to lock it in, now is the right time. I can't guarantee it'll be open next week."
        </div>
        <Button onClick={handleGeneratePDF} className="ctrl-pdf-btn" data-testid="generate-pdf-btn">
          <FileDown className="w-4 h-4" /> Send meeting recap to {lead.clientName}
        </Button>
        <p className="ctrl-note">Generates PDF with plan, price, what's included, payment link, and next steps</p>
      </div>
    );
  }

  return null;
}
